import React, { useState } from "react";
import PropTypes from "prop-types";
import { ArrowDown, ArrowUp, Image as ImageIcon, Layout, List, Plus, Trash2, Type, Printer } from "react-feather";
import { C } from "../../reporting/reportingStyles";
import PillTabs from "../../common/PillTabs";
import { Cols, Field, Section, SwitchField, inputStyle, smallInputStyle, secondaryBtnStyle, withDisabled, hintStyle } from "../../common/FormKit";
import MediaPickerModal from "./MediaPickerModal";
import {
  ALIGNS, FONT_WEIGHTS, LIMITS, LOCKED_KEYS, PINNED_FOOTER_KEYS, PLACEMENTS, ROW_HINTS, ROW_LABELS, SEGMENT_KEYS,
  clampNumber, countRows, newImageRow, newTextRow,
} from "./posReceiptTemplateMeta";

/**
 * Controlled editor for one template layout. `value` is the layout (always the full shape the
 * API returns); every change hands a new layout to `onChange`. Four tabs follow the receipt from
 * top to bottom. `readOnly` draws the same form without inputs enabled (system templates for a
 * non-admin, and read-only roles).
 */

const TABS = [
  { key: "header", label: "Header" },
  { key: "products", label: "Products" },
  { key: "totals", label: "Totals & payments" },
  { key: "footer", label: "Footer" },
];

// FormKit's Field draws the label text without htmlFor; the aria-label carries the same words so
// tests and screen readers find the input by what the admin sees.
const NumberField = ({ label, hint, value, onChange, range: [min, max], disabled }) => (
  <Field label={label} hint={hint}>
    <input
      type="number"
      aria-label={label}
      min={min}
      max={max}
      step={1}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => onChange(clampNumber(e.target.value, [min, max], min))}
      style={{ ...smallInputStyle, width: 90 }}
    />
  </Field>
);

const SelectField = ({ label, hint, value, onChange, options, disabled }) => (
  <Field label={label} hint={hint}>
    <select aria-label={label} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ ...smallInputStyle, width: "auto", minWidth: 120 }}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </Field>
);

const numberInput = (value, onChange, range, disabled) => ({ value, onChange, range, disabled });
const selectInput = (value, onChange, options, disabled) => ({ value, onChange, options, disabled });

const rowCard = (locked) => ({
  border: `1px solid ${locked ? C.coralLight : C.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: C.white,
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const miniBtn = (disabled) => withDisabled({ ...secondaryBtnStyle, padding: "4px 8px", fontSize: 12 }, disabled);

const PosReceiptTemplateEditor = ({ value, onChange, readOnly = false, merchantId }) => {
  const [tab, setTab] = useState("header");
  const [picker, setPicker] = useState(null); // { section, rowId } while choosing an image

  const layout = value;
  const set = (path, v) => {
    const next = JSON.parse(JSON.stringify(layout));
    let cur = next;
    for (let i = 0; i < path.length - 1; i += 1) {
      if (cur[path[i]] === undefined || cur[path[i]] === null) cur[path[i]] = {};
      cur = cur[path[i]];
    }
    cur[path[path.length - 1]] = v;
    onChange(next);
  };

  const setRows = (section, rows) => set([section, "rows"], rows);
  const updateRow = (section, index, patch) => {
    const rows = [...layout[section].rows];
    rows[index] = { ...rows[index], ...patch };
    setRows(section, rows);
  };
  const moveRow = (section, index, dir) => {
    const rows = [...layout[section].rows];
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    if (section === "footer" && PINNED_FOOTER_KEYS.includes(rows[target].key)) return;
    [rows[index], rows[target]] = [rows[target], rows[index]];
    setRows(section, rows);
  };
  const removeRow = (section, index) => setRows(section, layout[section].rows.filter((_, i) => i !== index));
  const insertRow = (section, row) => {
    const rows = [...layout[section].rows];
    // Custom rows go above the pinned native lines in the footer, at the end of the header.
    const at = section === "footer" ? rows.findIndex((r) => PINNED_FOOTER_KEYS.includes(r.key)) : -1;
    if (at === -1) rows.push(row); else rows.splice(at, 0, row);
    setRows(section, rows);
  };

  const renderRow = (section, row, index, rows) => {
    const locked = LOCKED_KEYS.includes(row.key);
    const pinned = section === "footer" && PINNED_FOOTER_KEYS.includes(row.key);
    const custom = row.key === "text" || row.key === "image";
    const label = ROW_LABELS[row.key] || row.key;
    const canMoveUp = !pinned && index > 0;
    const canMoveDown = !pinned && index < rows.length - 1 && !(section === "footer" && PINNED_FOOTER_KEYS.includes(rows[index + 1]?.key));
    const rowId = row.id || row.key;

    return (
      <div key={`${section}-${rowId}`} style={rowCard(locked)} data-testid={`row-${section}-${rowId}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text, flex: 1, minWidth: 140 }}>
            {label}
            {locked && <span style={{ fontSize: 10, fontWeight: 600, color: C.coral, marginLeft: 8, textTransform: "uppercase" }}>required</span>}
            {pinned && <span style={{ fontSize: 10, fontWeight: 600, color: C.textLight, marginLeft: 8, textTransform: "uppercase" }}>pinned last</span>}
          </span>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMed, margin: 0, cursor: locked ? "not-allowed" : "pointer" }}>
            <input
              type="checkbox"
              aria-label={`Show ${label}`}
              checked={row.visible !== false}
              disabled={readOnly || locked}
              onChange={(e) => updateRow(section, index, { visible: e.target.checked })}
            />
            Show
          </label>
          {!pinned && (
            <>
              <button type="button" aria-label={`Move ${label} up`} style={miniBtn(readOnly || !canMoveUp)} disabled={readOnly || !canMoveUp} onClick={() => moveRow(section, index, -1)}><ArrowUp size={12} /></button>
              <button type="button" aria-label={`Move ${label} down`} style={miniBtn(readOnly || !canMoveDown)} disabled={readOnly || !canMoveDown} onClick={() => moveRow(section, index, 1)}><ArrowDown size={12} /></button>
            </>
          )}
          {custom && (
            <button type="button" aria-label={`Remove ${label}`} style={miniBtn(readOnly)} disabled={readOnly} onClick={() => removeRow(section, index)}><Trash2 size={12} /></button>
          )}
        </div>
        {ROW_HINTS[row.key] && <span style={{ ...hintStyle, marginTop: 0 }}>{ROW_HINTS[row.key]}</span>}

        {row.key === "logo" && (
          <Cols min={120}>
            <NumberField label="Width (px)" {...numberInput(row.width, (v) => updateRow(section, index, { width: v }), LIMITS.logoWidth, readOnly)} />
            <NumberField label="Height (px)" {...numberInput(row.height, (v) => updateRow(section, index, { height: v }), LIMITS.logoHeight, readOnly)} />
          </Cols>
        )}

        {row.key === "zatcaQr" && (
          <Cols min={120}>
            <NumberField label="QR size" {...numberInput(row.size, (v) => updateRow(section, index, { size: v }), LIMITS.qrSize, readOnly)} />
          </Cols>
        )}

        {row.key === "text" && (
          <Cols min={160}>
            <Field label="English">
              <input type="text" aria-label="English" maxLength={LIMITS.textLength} value={row.en || ""} disabled={readOnly} placeholder="Text" style={smallInputStyle} onChange={(e) => updateRow(section, index, { en: e.target.value })} />
            </Field>
            <Field label="Arabic">
              <input type="text" aria-label="Arabic" dir="rtl" maxLength={LIMITS.textLength} value={row.ar || ""} disabled={readOnly} placeholder="نص" style={smallInputStyle} onChange={(e) => updateRow(section, index, { ar: e.target.value })} />
            </Field>
          </Cols>
        )}

        {row.key === "image" && (
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ width: 96, height: 64, border: `1px dashed ${C.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: C.bg }}>
              {row.url ? <img src={row.url} alt="" style={{ maxWidth: "100%", maxHeight: "100%" }} /> : <ImageIcon size={18} color={C.textLight} />}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 200 }}>
              <div>
                <button type="button" style={miniBtn(readOnly)} disabled={readOnly} onClick={() => setPicker({ section, rowId })}>
                  {row.url ? "Change image" : "Choose image"}
                </button>
              </div>
              <Cols min={110}>
                <NumberField label="Width (px)" {...numberInput(row.width, (v) => updateRow(section, index, { width: v }), LIMITS.imageWidth, readOnly)} />
                <NumberField label="Height (px)" {...numberInput(row.height, (v) => updateRow(section, index, { height: v }), LIMITS.imageHeight, readOnly)} />
                <SelectField label="Align" {...selectInput(row.align || "center", (v) => updateRow(section, index, { align: v }), ALIGNS, readOnly)} />
              </Cols>
            </div>
          </div>
        )}

        {!["logo", "zatcaQr", "phone", "image"].includes(row.key) && (
          <Cols min={120}>
            <NumberField label="Font size" {...numberInput(row.fontSize, (v) => updateRow(section, index, { fontSize: v }), LIMITS.fontSize, readOnly)} />
            <SelectField label="Weight" {...selectInput(row.fontWeight || "normal", (v) => updateRow(section, index, { fontWeight: v }), FONT_WEIGHTS, readOnly)} />
            <SelectField label="Align" {...selectInput(row.align || "center", (v) => updateRow(section, index, { align: v }), ALIGNS, readOnly)} />
            {SEGMENT_KEYS.includes(row.key) && (
              <NumberField label="Bold line size" {...numberInput(row.boldFontSize, (v) => updateRow(section, index, { boldFontSize: v }), LIMITS.boldFontSize, readOnly)} />
            )}
          </Cols>
        )}
      </div>
    );
  };

  const addButtons = (section) => {
    const rows = layout[section].rows;
    const textFull = countRows(rows, "text") >= LIMITS.textRows;
    const imageFull = countRows(rows, "image") >= LIMITS.imageRows;
    return (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" style={withDisabled(secondaryBtnStyle, readOnly || textFull)} disabled={readOnly || textFull} onClick={() => insertRow(section, newTextRow(section, rows))}>
          <Plus size={14} /> Text line
        </button>
        <button type="button" style={withDisabled(secondaryBtnStyle, readOnly || imageFull)} disabled={readOnly || imageFull} onClick={() => setPicker({ section, rowId: null })}>
          <Plus size={14} /> Image line
        </button>
        <span style={{ ...hintStyle, marginTop: 0, alignSelf: "center" }}>
          Up to {LIMITS.textRows} text and {LIMITS.imageRows} image lines per section.
        </span>
      </div>
    );
  };

  const onPickImage = (url) => {
    if (!picker) return;
    const { section, rowId } = picker;
    const rows = layout[section].rows;
    if (rowId) {
      const index = rows.findIndex((r) => (r.id || r.key) === rowId);
      if (index !== -1) updateRow(section, index, { url });
    } else {
      insertRow(section, newImageRow(section, rows, url));
    }
    setPicker(null);
  };

  const items = layout.items;
  const totals = layout.totals;

  return (
    <div>
      <PillTabs tabs={TABS} value={tab} onChange={setTab} ariaLabel="Template sections" />

      {tab === "header" && (
        <Section icon={Layout} title="Header" subtitle="Printed as one block above the call number. Drag order with the arrows; required rows can move but not hide.">
          <Cols min={160}>
            <NumberField label="Gap between rows (px)" {...numberInput(layout.header.rowGap, (v) => set(["header", "rowGap"], v), LIMITS.rowGap, readOnly)} />
          </Cols>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {layout.header.rows.map((row, i, rows) => renderRow("header", row, i, rows))}
          </div>
          {addButtons("header")}
        </Section>
      )}

      {tab === "products" && (
        <Section icon={List} title="Product lines" subtitle="Name, quantity, unit price and line total keep their columns; only the text changes.">
          <Cols min={140}>
            <NumberField label="Font size" {...numberInput(items.fontSize, (v) => set(["items", "fontSize"], v), LIMITS.fontSize, readOnly)} />
            <SelectField label="Weight" {...selectInput(items.fontWeight, (v) => set(["items", "fontWeight"], v), FONT_WEIGHTS, readOnly)} />
            <NumberField label="Other lines size" hint="Customer, user, Add Ons, notes, calories." {...numberInput(items.secondaryFontSize, (v) => set(["items", "secondaryFontSize"], v), LIMITS.fontSize, readOnly)} />
            <NumberField label="Gap between items (px)" {...numberInput(items.rowGap, (v) => set(["items", "rowGap"], v), LIMITS.rowGap, readOnly)} />
          </Cols>
          <SwitchField checked={!!items.rowDivider} disabled={readOnly} onChange={(v) => set(["items", "rowDivider"], v)} label="Line under each item" hint="A rule below every product block, as on the Large template." />

          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMed, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>Arabic product name</div>
          <SwitchField checked={items.arabicName.show !== false} disabled={readOnly} onChange={(v) => set(["items", "arabicName", "show"], v)} label="Show the Arabic product name" />
          <Cols min={160}>
            <SelectField label="Placement" {...selectInput(items.arabicName.placement, (v) => set(["items", "arabicName", "placement"], v), PLACEMENTS, readOnly || items.arabicName.show === false)} />
            <NumberField label="Arabic font size" hint="Used for the next-line placement (product and modifier)." {...numberInput(items.arabicName.fontSize, (v) => set(["items", "arabicName", "fontSize"], v), LIMITS.fontSize, readOnly || items.arabicName.show === false)} />
          </Cols>

          <div style={{ fontSize: 12, fontWeight: 700, color: C.textMed, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>Modifiers (Add Ons)</div>
          <SwitchField checked={items.modifiers.show !== false} disabled={readOnly} onChange={(v) => set(["items", "modifiers", "show"], v)} label="Print modifiers on the customer receipt" hint="Kitchen tickets always print them." />
          <SwitchField checked={items.modifiers.arabic.show !== false} disabled={readOnly || items.modifiers.show === false} onChange={(v) => set(["items", "modifiers", "arabic", "show"], v)} label="Show the Arabic modifier name" />
          <Cols min={160}>
            <SelectField label="Placement" {...selectInput(items.modifiers.arabic.placement, (v) => set(["items", "modifiers", "arabic", "placement"], v), PLACEMENTS, readOnly || items.modifiers.show === false || items.modifiers.arabic.show === false)} />
          </Cols>
        </Section>
      )}

      {tab === "totals" && (
        <Section icon={Type} title="Totals & payment lines" subtitle="Amount, discount, net, VAT, total, change and one line per payment. Rows are fixed; the text can change.">
          <Cols min={140}>
            <NumberField label="Font size" {...numberInput(totals.fontSize, (v) => set(["totals", "fontSize"], v), LIMITS.fontSize, readOnly)} />
            <SelectField label="Weight" {...selectInput(totals.fontWeight, (v) => set(["totals", "fontWeight"], v), FONT_WEIGHTS, readOnly)} />
            <NumberField label="Gap between rows (px)" {...numberInput(totals.rowGap, (v) => set(["totals", "rowGap"], v), LIMITS.rowGap, readOnly)} />
          </Cols>
          <SwitchField checked={!!totals.vatDivider} disabled={readOnly} onChange={(v) => set(["totals", "vatDivider"], v)} label="Line under the VAT row" />
        </Section>
      )}

      {tab === "footer" && (
        <>
          <Section icon={Layout} title="Footer" subtitle="Printed under the payment lines. Phone and the QR code are native printer lines and always come last.">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {layout.footer.rows.map((row, i, rows) => renderRow("footer", row, i, rows))}
            </div>
            {addButtons("footer")}
          </Section>
          <div style={{ height: 14 }} />
          <Section icon={Printer} title="Epson" subtitle="Printer commands around the receipt.">
            <SwitchField checked={!!layout.epson?.feedBeforeCallNumber} disabled={readOnly} onChange={(v) => set(["epson", "feedBeforeCallNumber"], v)} label="Blank line before the call number" hint="Adds space between the header and the large call number, as on the Large template." />
          </Section>
        </>
      )}

      <MediaPickerModal isOpen={!!picker} toggle={() => setPicker(null)} merchantId={merchantId} onPick={onPickImage} />
    </div>
  );
};

PosReceiptTemplateEditor.propTypes = {
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export { inputStyle };
export default PosReceiptTemplateEditor;
